import { createTheme, ThemeProvider } from "@vitalsource/vstui/themes";
import React, { ReactNode, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { cheddar, ocean, magic } from "@vitalsource/vstui/colors";
import GlobalStyleProvider from "./GlobalStyleProvider";
import en from "@vitalsource/vstui/i18n/translations/en";
import deepmerge from "deepmerge";
import { getVstuiLocaleFromFullLocale } from "../utils/locale";

interface VSTUIProviderProps {
  children: ReactNode;
  theme?: {
    base: any;
    primary: any;
    secondary: any;
  };
}

const styles = `
  html {
    font-size: 62.5%;
  }
`;

export default function VSTUIProvider({ children, theme }: VSTUIProviderProps) {
  const [messages, setMessages] = useState();
  const { locale } = useIntl();
  useEffect(() => {
    const vstuiLocale = getVstuiLocaleFromFullLocale(locale);
    if (vstuiLocale === "en") {
      setMessages(en);
    } else {
      import(`@vitalsource/vstui/i18n/translations/${vstuiLocale}`)
        .then((response) => {
          // This merge is covering for vstui throwing an exception when it
          // encounters an untranslated message, which causes the whole component
          // tree to unmount. If that gets fixed in general, we can take this out.
          setMessages(deepmerge(en, response.default));
        })
        .catch(() => setMessages(en));
    }
  }, [locale]);

  if (!messages) {
    return null;
  }

  const vstuiTheme = createTheme({
    color: {
      primary: theme ? theme.primary : ocean,
      secondary: theme ? theme.secondary : cheddar,
    },
    typography: {
      htmlFontSize: 10,
    },
    i18n: messages,
  });

  const localTheme = {
    color: {
      base: theme ? theme.base : magic,
    },
  };

  return (
    <>
      <GlobalStyleProvider styles={styles} />
      <ThemeProvider theme={{ ...vstuiTheme, ...localTheme }}>
        {children}
      </ThemeProvider>
    </>
  );
}
